/**
 * Symbol Tool - Symbol Placement
 * 
 * Places generic annotation symbols.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export interface SymbolGeometryData {
    position: [number, number, number];
}

export interface SymbolElement extends Element {
    category: 'Generic Annotations';
    geometryData: SymbolGeometryData;
}

export class SymbolTool extends BaseTool {
    id = 'symbol';
    name = 'Symbol';
    cursor = 'crosshair';

    // Options (BIM Symbol panel: north arrow, break line, centerline)
    private symbolType: 'North Arrow' | 'Break Line' | 'Centerline' = 'North Arrow';

    /** Resolve the current Options Bar symbol at use time: switching the
     *  dropdown while the tool stays active must affect the next placement. */
    private currentSymbolType(): 'North Arrow' | 'Break Line' | 'Centerline' {
        return this.context?.bimStore.toolProperties.symbolType ?? this.symbolType;
    }

    protected onActivate(): void {
        this.symbolType = this.currentSymbolType();
        this.notify(i18n.t('tools.notify.symbolPick', { symbolType: this.symbolType }));
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.createSymbol(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'symbol',
                position: snapped
            });
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createSymbol(point: THREE.Vector3) {
        if (!this.context) return;

        // No scale is baked into the symbol: annotations resize live with the
        // active view scale at render time (see SymbolAnnotationRenderer),
        // so switching 1:100 -> 1:50 updates every placed symbol instantly.
        const symbolType = this.currentSymbolType();

        const geometryData: SymbolGeometryData = {
            position: [point.x, point.y, point.z]
        };

        const symbol: SymbolElement = {
            id: uuidv4(),
            category: 'Generic Annotations',
            type: symbolType,
            parameters: {
                'Type': symbolType
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(symbol);
    }
}
